import React, {
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";

import DropdownMultiSelect from "shared/components/inputs/dropdown-multi-select/DropdownMultiSelect";
import { DropdownMultiSelectContext } from "shared/contexts/DropdownMultiSelectContext";
import FormInputWrapper from "shared/components/inputs/form/FormInputWrapper";
import PropTypes from "prop-types";
import SegmentedControl from "shared/components/inputs/segmented-control/SegmentedControl";
import { ServerSidePaginationContext } from "shared/contexts/ServerSidePaginationContext";
import {
    StyledDropdown,
    StyledFormInputWrapper,
} from "./TableFilters.styles";

const TableFilters = ({
    initialFilterData = [],
    filtersConfig = [],
    autoUpdateFilterData = [],
    onChange = () => void 0,
    handleOnChangeDropdownColumn,
}) => {
    const { multiSelectDropdownOptionsList } = useContext(
        DropdownMultiSelectContext
    );
    const [count, setCount] = useState(0);
    const { queryPageIndexUpdate } = useContext(ServerSidePaginationContext);
    const internalFiltersState = useRef(
        new Map(
            filtersConfig?.map((filter) => [
                filter.name,
                filter.defaultValue ?? filter.unfilteredValue,
            ])
        )
    );

    const externalStateUpdater = useCallback(() => {
        const filterList = [];
        internalFiltersState?.current?.forEach((value, name) => {
            const filter = filtersConfig?.find(
                (filterName) => filterName.name === name
            );

            if (filter && value !== filter.unfilteredValue) {
                if (
                    (filter.hasOwnProperty("show") && filter.show) ||
                    !filter.hasOwnProperty("show")
                ) {
                    filterList.push({
                        id: filter.columnAccessor,
                        value,
                    });
                }
            }
        });

        onChange(filterList);
        queryPageIndexUpdate(0);
    }, [filtersConfig, onChange, queryPageIndexUpdate]);

    useEffect(() => {
        setCount((prev) => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- internalFiltersState.current is a mutable ref used intentionally to trigger re-renders when filters are updated
    }, [internalFiltersState.current]);

    useEffect(() => {
        for (const initialFilter of initialFilterData || []) {
            internalFiltersState.current.set(
                initialFilter.name,
                initialFilter.value
            );
            externalStateUpdater();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally gated on count only; adding initialFilterData/externalStateUpdater would cause infinite loops via onChange re-renders
    }, [count]);

    useEffect(() => {
        if (autoUpdateFilterData?.length > 0) {
            for (const x of autoUpdateFilterData) {
                internalFiltersState.current.set(x.name, x.value);
                externalStateUpdater();
            }
        }
    }, [autoUpdateFilterData, externalStateUpdater]);

    return filtersConfig.map((filter) => {
        const onChangeInternal = (newValue) => {
            internalFiltersState.current.set(filter.name, newValue);
            externalStateUpdater();
        };

        let displayDropdownControl = filter.isDropdownControl;

        if (filter?.hasOwnProperty("show")) {
            displayDropdownControl = filter.show;
        }

        return (
            <Fragment key={filter.name}>
                {filter.isSegmentedControl && (
                    <SegmentedControl
                        name={filter.name}
                        value={internalFiltersState.current.get(filter.name)}
                        options={filter.options}
                        onChange={onChangeInternal}
                        filterLabel={filter.filterLabel}
                        disabled={filter.disabled}
                    />
                )}
                {filter.showColumnFilter && (
                    <FormInputWrapper
                        isSmallSize={true}
                        label={filter.name}
                        style={{
                            width: filter.formInputWrapperWidth ?? "160px",
                        }}
                    >
                        <DropdownMultiSelect
                            isSmallSize={true}
                            isVariantToggle={true}
                            initialData={multiSelectDropdownOptionsList}
                            optionsData={multiSelectDropdownOptionsList}
                            onChangeDropdown={handleOnChangeDropdownColumn}
                        />
                    </FormInputWrapper>
                )}
                {displayDropdownControl && (
                    <StyledFormInputWrapper
                        label={filter.dropdownFilterLabel}
                        isSmallSize={true}
                        className={filter.formInputWrapperClassName}
                    >
                        <StyledDropdown
                            options={filter.options}
                            value={internalFiltersState.current.get(
                                filter.name
                            )}
                            onChange={onChangeInternal}
                            isSmallSize={true}
                        />
                    </StyledFormInputWrapper>
                )}
            </Fragment>
        );
    });
};

TableFilters.propTypes = {
    initialFilterData: PropTypes.array,
    autoUpdateFilterData: PropTypes.array,
    handleOnChangeDropdownColumn: PropTypes.func,
    filtersConfig: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            // columnAccessor: PropTypes.string.isRequired,
            isSegmentedControl: PropTypes.bool,
            isDropdownControl: PropTypes.bool,
            formInputWrapperClassName: PropTypes.string,
            unfilteredValue: PropTypes.string,
            defaultValue: PropTypes.string,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                    ]).isRequired,
                    label: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                    ]).isRequired,
                })
            ),
            dropdownFilterLabel: PropTypes.string,
        })
    ),
    onChange: PropTypes.func,
    isSegmentedControl: PropTypes.bool,
    isDropdownControl: PropTypes.bool,
};

export default TableFilters;
